import moment from "moment";
import { TFunction } from "i18next";
import { MessageResponse } from "../types/requesterTypes";
import { Materials } from "../types/types";
import { AlarmEnum, getUnit, NodeTypes, ProbeTypes, TSensorTypes } from "../types/generated_types";
import { Roles } from "../types/types";
import { specialTypeName } from "../components/Alarms/util/utils";
import { ConstraintEnum } from "../components/Alarms/types";

export function cleanUpDateTime(dtString: string | null | undefined): string {
  if (dtString && dtString !== "0000-00-00 00:00:00") {
    return moment.utc(dtString).local().format("YYYY-MM-DD HH:mm:ss");
  }
  return "0000-00-00 00:00:00";
}

// This function is used to check if an error was returned from an endpoint
export function isMessageResponse<T>(err: MessageResponse | T): err is MessageResponse {
  return err !== undefined && (err as MessageResponse).status !== undefined;
}

export function getProductName(input: NodeTypes, t: TFunction<string>): string {
  switch (input) {
    case NodeTypes.IMI:
      return t("Integration Box");
    case NodeTypes.CC4:
      return t("CC4 Box");
    case NodeTypes.IMR:
    case NodeTypes.IMRMk2:
      return t("Relay Box");
    case NodeTypes.IMS:
    case NodeTypes.IMSMk2:
      return t("Sensor Box");
    case NodeTypes.AD9:
      return t("Dehumidifier");
    default:
      return input;
  }
}

export function transformVisibleRoleTypes(arg: Roles, t: TFunction<string>): string {
  switch (arg) {
    case Roles.Admin: {
      return t("Admin");
    }
    case Roles.CompanyUser: {
      return t("CompanyUser");
    }
    case Roles.Supervisor: {
      return t("Supervisor");
    }
    case Roles.User: {
      return t("User");
    }
  }
}

export function getProbeName(input: ProbeTypes | null, t: TFunction<string>): string {
  /* NOTE: Maintain together with back-end's PROBE_NAMES
   *       in backend/reports/helpers.py
   */
  switch (input) {
    case ProbeTypes.OHM:
      return t("Resistive");
    case ProbeTypes.RH:
      return t("RH/TEMP");
    case ProbeTypes.VOC:
      return t("IAQ");
    default:
      return t("Unknown");
  }
}

// Maintain together with `style_format()` in
// 'backend/reports/helpers.py'
export function roundData(
  latestData: string | null,
  sensorType: TSensorTypes,
  t: TFunction<string>,
  material?: Materials,  // unused since IMCS-730
): string {
  if (latestData === null) {
    return "--";
  } else {
    switch (sensorType) {
      case "PDV":
      case "WCT":
      case "GPK":
        return Number(latestData).toFixed(2);
      case "TMP":
      case "HUM":
      case "DWT":
        return Number(latestData).toFixed(1);
      case "WME":
        return Number(latestData).toFixed(1);
      case "HML":
        return Number(latestData).toFixed(0);
      case "PWR":
        return Number(latestData).toFixed(3);
      case "VOC":
      case "CO2":
      case "OHR":
        return Number(latestData).toFixed(0);
      case "STE":
      case "STE1":
      case "STE2":
      case "STE3":
      case "STE4":
        if (latestData === "0") {
          return t("OFF");
        } else {
          return t("ON");
        }
      case "MC":
        return Number(latestData).toFixed(1);
      case "OHM":
        return Number(latestData).toFixed(2);
      case "HEAT":
        if (latestData === "0") return t("Disabled");
        if (latestData === "1") return t("Enabled");
        return latestData;
      case "FMODE":
        if (latestData === "0") return t("Normal");
        if (latestData === "1") return t("Continuous");
        return latestData;
      case "SPEED":
        if (latestData === "0") return t("Slow");
        if (latestData === "1") return t("Normal");
        if (latestData === "2") return t("Night");
        return latestData;
      default:
        return Number(latestData).toFixed(1);
    }
  }
}

// Calculates moisture content value based on wood moisture equivalent value
// Formulae provided in IMCS-342
export function calculateMCValue(wmeValue: number, material: Materials) : number {
  /* NOTE: Maintain together with back-end's calculate_MC_value()
   *       in backend/reports/helpers.py
   */
  switch (material) {
    case Materials.WOOD:
      return wmeValue;
    case Materials.PLAS:
      return 0.176974 * wmeValue - 2.47171;
    case Materials.BRIC:
      return 0.374781 * wmeValue - 4.33092;
    case Materials.CEME:
      return 0.520614 * wmeValue - 5.64342;
    case Materials.SAND:
      return 0.302564 * wmeValue + 1.22821;
    case Materials.CONC:
      return 0.215187 * wmeValue + 1.44398;
    default:
      return 0;
  }
}

export function isNonChartableRow(sensorType: TSensorTypes) {
  return [
    TSensorTypes.OHR,
  ].includes(sensorType);
}

/** Check if string a contains string b, case insensitve*/
export function contains(a: string | undefined | null, b: string) {
  return a?.toLocaleLowerCase().includes(b.toLocaleLowerCase()) ?? false
}

/** Generate alarm trigger condition text */
export function generateAlarmText(
  alarmType: AlarmEnum,
  constraint: ConstraintEnum | undefined,
  thresholdValue: number | undefined,
  t: TFunction<string>,
) {
  switch(alarmType){
    case AlarmEnum.PAS:
    case AlarmEnum.RED:
    case AlarmEnum.WFL:
      return specialTypeName(alarmType, t);
    default:
      return `${specialTypeName(alarmType, t)} ${constraint} ${thresholdValue}
      ${getUnit(alarmType)}`;
  }
}

/**
 * Gets a shortened month name
 * @param month number of the month (0-11)
 */
export function getShortMonth(month: number, t: TFunction) {
  if (!Number.isInteger(month) || month > 11 || month < 0) return ""
  return [
    t("Jan"), t("Feb"), t("Mar"),
    t("Apr"), t("May"), t("Jun"),
    t("Jul"), t("Aug"), t("Sep"),
    t("Oct"), t("Nov"), t("Dec"),
  ][month]
}